import { useSession } from "@/api/session";
import { useModoInstalacao } from "@/config/InfoApp";

export type CanalPermissao = "Backoffice" | "POS";

const PERMISSAO_MAE: Record<CanalPermissao, string> = {
  Backoffice: "BACBOF",
  POS: "POSPDV",
};

/** Espelha UsuarioService.confirmPermissao (Java): ADMIN/SADMIN têm sempre
 * acesso; um OPERADOR precisa da permissão "mãe" do canal (BACBOF/POSPDV)
 * e do código específico. Use directamente no template: `v-if="temPermissao('BACGDO', 'Backoffice')"`. */
export function temPermissao(
  codigoPermissao: string,
  canal: CanalPermissao,
): boolean {
  const { currentUser } = useSession();
  const user = currentUser.value;
  if (!user) return false;
  if (user.grupo === "ADMIN" || user.grupo === "SADMIN") return true;

  const permissoes =
    canal === "Backoffice"
      ? user.minhasPermissoes.backoffice
      : user.minhasPermissoes.pos;

  if (!permissoes.includes(PERMISSAO_MAE[canal])) return false;
  return permissoes.includes(codigoPermissao);
}

/** Só ADMIN/SADMIN — usar para acções administrativas de emergência sem
 * permissão granular associada (ex.: fechar caixa/reserva pelo Backoffice). */
export function ehAdmin(): boolean {
  const { currentUser } = useSession();
  const grupo = currentUser.value?.grupo;
  return grupo === "ADMIN" || grupo === "SADMIN";
}

/** Numa instalação OFFLINE (modo obtido da API — ver `@/config/InfoApp`),
 * Integração de APIs, Configuração das chaves FE, assinar/renovar licença e
 * criar conta ficam indisponíveis para TODOS os utilizadores, mesmo com
 * permissão para tal. */
export function estaOffline(): boolean {
  const { ulemoLicence } = useModoInstalacao();
  return ulemoLicence.value === "OFFLINE";
}

/** Instalação (Constants.estaEmProducao no Java) já em produção — nada a ver com
 * o "Ambiente de teste" por comerciante mostrado no Topbar. Usar para decidir se
 * "Criar conta" pode ficar visível/acessível antes do login. */
export function estaEmProducao(): boolean {
  const { estaEmProducao: flag } = useModoInstalacao();
  return flag.value;
}

/** Integração de API é exclusiva de assinaturas pagas (com pacote associado):
 * sem assinatura, ou com uma assinatura sem pacote (ex.: teste grátis), não
 * há acesso. */
export function assinaturaGratuita(): boolean {
  const { currentUser } = useSession();
  const assinatura = currentUser.value?.comerciante.assinatura;
  return !assinatura || assinatura.gratuita;
}
